/**

 * Short-lived in-memory cache for shared reference GETs (e.g. /users).

 * Not an auth/permission layer. Cleared on authenticated user boundary.

 */



/** @typedef {{ data: any, fetchedAt: number, key: string }} ReferenceCacheEntry */



export const REFERENCE_STALE_MS = Object.freeze({

  users: 10 * 60_000,

  material: 30 * 60_000,

  default: 5 * 60_000,

});



const MAX_ENTRIES = 20;



/** @type {Map<string, ReferenceCacheEntry>} */

const store = new Map();



function touch(key, entry) {

  store.delete(key);

  store.set(key, entry);

  while (store.size > MAX_ENTRIES) {

    const oldest = store.keys().next().value;

    store.delete(oldest);

  }

}



export function getReferenceStaleMs(domain) {

  if (domain && REFERENCE_STALE_MS[domain] != null) return REFERENCE_STALE_MS[domain];

  return REFERENCE_STALE_MS.default;

}



/**

 * @param {string} key

 * @param {{ staleTime?: number, now?: number }} [opts]

 * @returns {{ entry: ReferenceCacheEntry|null, fresh: boolean, stale: boolean }}

 */

export function getReferenceCache(key, opts = {}) {

  if (!key) return { entry: null, fresh: false, stale: false };

  const entry = store.get(key) || null;

  if (!entry) return { entry: null, fresh: false, stale: false };

  touch(key, entry);

  const staleTime = opts.staleTime ?? REFERENCE_STALE_MS.default;

  const now = opts.now ?? Date.now();

  const age = now - entry.fetchedAt;

  const fresh = age <= staleTime;

  return { entry, fresh, stale: !fresh };

}



/**

 * @param {string} key

 * @param {any} data

 */

export function setReferenceCache(key, data) {

  if (!key) return;

  const entry = {

    key,

    data,

    fetchedAt: Date.now(),

  };

  touch(key, entry);

  return entry;

}



/**

 * Invalidate by exact key or prefix.

 * @param {string} keyOrPrefix

 * @param {{ prefix?: boolean }} [opts]

 */

export function invalidateReferenceCache(keyOrPrefix, opts = {}) {

  if (!keyOrPrefix) return 0;

  if (!opts.prefix) {

    return store.delete(keyOrPrefix) ? 1 : 0;

  }

  let n = 0;

  for (const key of [...store.keys()]) {

    if (key.startsWith(keyOrPrefix)) {

      store.delete(key);

      n += 1;

    }

  }

  return n;

}



export function clearAllReferenceCache() {

  const n = store.size;

  store.clear();

  return n;

}



/** Test / debug helper */

export function __referenceCacheSize() {

  return store.size;

}



/** Test helper */

export function __referenceCacheKeys() {

  return [...store.keys()];

}



if (typeof window !== "undefined") {

  window.__WT_REFERENCE_CACHE__ = {

    clearAllReferenceCache,

    getReferenceCache,

    setReferenceCache,

    invalidateReferenceCache,

    size: __referenceCacheSize,

  };

}

